import React, { useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import { EmptyCard, NoAvailableCard } from 'common';
import { func, shape } from 'prop-types';
import { format } from 'date-fns';

import {
  useGetAllocationGroupsQuery,
  useGetInventoryDailyProjectionsQuery,
  useGetInventoryProjectionsQuery,
  useGetProductGroupsQuery,
} from '../dashboardEndpoints';
import FilterPanel from './FilterPanel';
import Averages from './Averages';
import FutureProjections from './FutureProjections';
import MaxDonut from './MaxDonut';
import MinDonut from './MinDonut';
import MaxProjectionCards from './MaxProjectionCards';
import FutureProjectionDrawer from './FutureProjectionDrawer';

const Projections = ({ filters, setFilters }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { currentAffiliate } = useUserRolePermissions();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const productGroup = R.defaultTo('', R.prop('productGroup', filters));
  const allocationGroup = R.defaultTo('', R.prop('allocationGroup', filters));

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
  };

  const allocationParams = {
    ...params,
    reportType: 'InventoryProjections',
  };

  const { data: allocationGroups = [], isFetching: isFetchingAllocationGroups = false } =
    useGetAllocationGroupsQuery({ params: allocationParams }, { skip: R.isNil(currentAffiliate) });

  const productParams = {
    ...allocationParams,
    allocationGroupId: R.prop('groupId', allocationGroup),
  };

  const { data: productGroups = [], isFetching: isFetchingProductGroups = false } =
    useGetProductGroupsQuery(
      { params: productParams },
      { skip: R.or(R.isNil(R.prop('groupId', allocationGroup)), R.isNil(currentAffiliate)) }
    );

  const projectionParams = {
    ...params,
    allocationGroupId: R.prop('groupId', allocationGroup),
    productSummaryGroupId: R.prop('groupId', productGroup),
  };

  const { data: dailyInventoryProjections, isFetching: fetchingDaily } =
    useGetInventoryDailyProjectionsQuery(
      { params: projectionParams },
      { skip: R.or(R.isEmpty(productGroup), R.isEmpty(allocationGroup)) }
    );

  const { data: inventoryProjections, isFetching: fetchingProjections } =
    useGetInventoryProjectionsQuery(
      { params: projectionParams },
      { skip: R.or(R.isEmpty(productGroup), R.isEmpty(allocationGroup)) }
    );

  const isFetchingData = R.or(fetchingDaily, fetchingProjections);

  const noProjections = R.and(
    R.isNil(R.prop('thirtyDaysLoading', inventoryProjections)),
    R.isEmpty(dailyInventoryProjections)
  );

  return (
    <>
      <FilterPanel
        allocationGroups={allocationGroups}
        filters={filters}
        isFetching={R.or(isFetchingAllocationGroups, isFetchingProductGroups)}
        productGroups={productGroups}
        setFilters={setFilters}
      />
      {R.isNil(inventoryProjections) && (
        <EmptyCard
          subtitle="Select a Pipeline System and Product Group you wish to see projections for."
          title="Which Inventory Projection do you wish to see?"
        />
      )}
      {noProjections && <NoAvailableCard type="projections" />}
      {isFetchingData && (
        <CircularProgress
          size={48}
          sx={{
            bottom: 0,
            color: 'secondary.main',
            left: 0,
            margin: 'auto',
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            top: 0,
          }}
        />
      )}
      {R.not(R.isNil(R.prop('thirtyDaysLoading', inventoryProjections))) && (
        <Averages inventoryProjections={inventoryProjections} />
      )}
      {R.not(R.isNil(inventoryProjections)) &&
        R.not(R.isNil(R.prop('max', inventoryProjections))) &&
        R.not(R.isNil(R.prop('min', inventoryProjections))) && (
          // eslint-disable-next-line sort-keys-fix/sort-keys-fix
          <Stack spacing={6} sx={{ mt: { xs: 2, lg: 6 } }}>
            <Stack alignItems="center" direction="row" spacing={1}>
              <Typography variant="subtitle2">TODAY</Typography>
              <Typography variant="body2">{format(new Date(), 'MM/dd/yyyy')}</Typography>
              <Tooltip
                placement="bottom"
                slotProps={{
                  popper: { sx: { maxWidth: 200, p: 1, textAlign: 'center' } },
                }}
                title="The Projected Min/Max update every day at midnight."
              >
                <InfoRoundedIcon
                  sx={{ '&:hover': { cursor: 'pointer' }, color: 'graphic.dark', fontSize: 16 }}
                />
              </Tooltip>
            </Stack>
            <Stack alignItems="center" direction={lgUp ? 'row' : 'column'} justifyContent="center">
              <MaxDonut data={R.prop('max', inventoryProjections)} />
              <MinDonut data={R.prop('min', inventoryProjections)} />
            </Stack>
            <MaxProjectionCards
              data={R.path(['max', 'projections', 'total'], inventoryProjections)}
            />
          </Stack>
        )}
      {R.and(R.not(noProjections), R.not(R.isNil(dailyInventoryProjections))) && (
        <FutureProjections
          dailyProjections={dailyInventoryProjections}
          setIsDrawerOpen={setIsDrawerOpen}
          setSelectedDate={setSelectedDate}
        />
      )}
      <FutureProjectionDrawer
        isDrawerOpen={isDrawerOpen}
        params={projectionParams}
        selectedDate={selectedDate}
        setIsDrawerOpen={setIsDrawerOpen}
        setSelectedDate={setSelectedDate}
      />
    </>
  );
};

Projections.propTypes = {
  filters: shape({}).isRequired,
  setFilters: func.isRequired,
};

export default Projections;
